import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import FilterTabs, { FilterTabOption } from '@/components/common/FilterTabs';

import { useNotificationStore } from '@/stores/notificationStore';
import {
  NOTIFICATION_TYPE_LABEL,
  NOTIFICATION_TYPE_ICONS,
  NOTIFICATION_TYPE_COLORS,
  getItemTargetUrl,
  formatNotificationTime,
} from '@/utils/notification';

type NotificationTabKey =
  | 'all'
  | 'SYSTEM'
  | 'CERTIFICATION'
  | 'DEMAND'
  | 'APPLICATION_REVIEW'
  | 'CONTRACT'
  | 'REVENUE_STATEMENT'
  | 'CATALOG_ORDER'
  | 'COMMUNITY';

const filterOptions: FilterTabOption<NotificationTabKey>[] = [
  { label: '系统通知', value: 'SYSTEM' },
  { label: '认证审核', value: 'CERTIFICATION' },
  { label: '需求消息', value: 'DEMAND' },
  { label: '应用审核', value: 'APPLICATION_REVIEW' },
  { label: '合同消息', value: 'CONTRACT' },
  { label: '收益结算', value: 'REVENUE_STATEMENT' },
  { label: '服务订单', value: 'CATALOG_ORDER' },
  { label: '社区通知', value: 'COMMUNITY' },
];

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState<NotificationTabKey>('all');
  const {
    unreadCount,
    messages,
    loading,
    loadingMore,
    hasMore,
    fetchMessages,
    fetchMoreMessages,
    markAsRead,
    markAllAsRead,
  } = useNotificationStore();

  useEffect(() => {
    void fetchMessages({
      page: 1,
      page_size: 10,
      message_type: activeTab === 'all' ? undefined : activeTab,
    });
  }, [activeTab, fetchMessages]);

  // 监听窗口整页滚动事件，触发触底加载下一页消息
  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const clientHeight = window.innerHeight || document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 120) {
        if (!loading && !loadingMore && hasMore) {
          void fetchMoreMessages({
            message_type: activeTab === 'all' ? undefined : activeTab,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeTab, loading, loadingMore, hasMore, fetchMoreMessages]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50/50">
      <Navbar />

      <div className="pt-28 sm:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 bg-white p-6 rounded-2xl border border-slate-100 shadow-xs">
            <div>
              <h1 className="font-heading font-bold text-xl text-slate-900 flex items-center gap-2">
                <span>消息中心</span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500 text-white">
                    {unreadCount} 条未读
                  </span>
                )}
              </h1>
              <p className="text-xs text-slate-500 mt-1">
                {unreadCount > 0 ? `有 ${unreadCount} 条未读站内通知` : '暂无未读消息，所有通知已处理'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer"
              >
                <i className="ri-check-double-line text-sm"></i>
                <span>全部标记已读</span>
              </button>
            )}
          </div>

          {/* Tabs */}
          <FilterTabs<NotificationTabKey>
            options={filterOptions}
            value={activeTab}
            onChange={(val) => setActiveTab(val)}
            showAll={true}
            allLabel="全部"
            allValue="all"
            popoverTitle="消息类型筛选"
            className="mb-6"
            activeItemClassName="bg-emerald-500 text-white font-medium shadow-md shadow-emerald-500/20 scale-[1.02]"
            inactiveItemClassName="border border-slate-200/80 bg-white text-slate-600 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50/60 hover:shadow-xs"
          />

          {/* Notifications List */}
          <div className="space-y-3">
            {loading && messages.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                <div className="w-8 h-8 mx-auto mb-3 border-3 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                <p className="text-slate-400 text-xs">正在加载消息数据...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                  <i className="ri-notification-off-line text-2xl text-slate-400"></i>
                </div>
                <p className="text-sm font-medium text-slate-600">暂无相关消息通知</p>
              </div>
            ) : (
              <>
                {messages.map((notification) => {
                  const targetUrl = getItemTargetUrl(notification);
                  const isRead = Boolean(notification.is_read);
                  const msgType = notification.message_type || 'SYSTEM';

                  return (
                    <Link
                      key={notification.id}
                      to={targetUrl}
                      onClick={() => {
                        if (!isRead && notification.id) {
                          void markAsRead(notification.id);
                        }
                      }}
                      className={`block bg-white rounded-2xl p-5 border transition-all cursor-pointer hover:shadow-md hover:-translate-y-0.5 ${!isRead
                          ? 'border-emerald-200/80 bg-gradient-to-r from-emerald-50/20 via-white to-white'
                          : 'border-slate-100 opacity-90'
                        }`}
                    >
                      <div className="flex gap-4">
                        {/* Icon */}
                        <div
                          className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${NOTIFICATION_TYPE_COLORS[msgType] || 'bg-slate-100 text-slate-600'
                            }`}
                        >
                          <i
                            className={`${NOTIFICATION_TYPE_ICONS[msgType] || 'ri-notification-3-line'
                              } text-base`}
                          ></i>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <h3
                              className={`text-sm leading-snug ${!isRead
                                  ? 'font-bold text-slate-900'
                                  : 'font-medium text-slate-700'
                                }`}
                            >
                              {notification.title}
                            </h3>
                            {!isRead && (
                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1 shadow-2xs"></span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-2.5">
                            {notification.content}
                          </p>
                          <div className="flex items-center justify-between text-xs text-slate-400">
                            <span className="flex items-center gap-1 text-slate-400">
                              <i className="ri-time-line"></i>
                              {formatNotificationTime(notification.created_at)}
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                              {NOTIFICATION_TYPE_LABEL[msgType] || msgType}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}

                {/* 触底加载更多 Indicator */}
                {loadingMore && (
                  <div className="py-6 text-center flex items-center justify-center gap-2 bg-white rounded-2xl border border-slate-100 shadow-xs">
                    <div className="w-5 h-5 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
                    <span className="text-xs text-slate-500 font-medium">正在加载更多通知...</span>
                  </div>
                )}
                {!hasMore && messages.length >= 5 && (
                  <div className="py-6 text-center text-xs text-slate-400 font-medium">
                    已加载全部消息通知
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}